import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Box, Typography, Container, CircularProgress } from "@mui/material";
import { useLoginModal } from "./contexts/LoginModalContext";
import { LoginModal } from "./components/auth/LoginModal";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { Header } from "./components/layout/Header";
import { MobileMenu } from "./components/layout/MobileMenu";
import { Footer } from "./components/layout/Footer";
import { Hero } from "./components/home/Hero";
import { FeaturedGalleries } from "./components/home/FeaturedGalleries";
import { PageTransition } from "./components/common/PageTransition";
import { ScrollToTop } from "./components/common/ScrollToTop";
import { GalleryDetailPage } from "./pages/GalleryDetailPage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { SitemapPage } from "./pages/SitemapPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { GalleryManagementPage } from "./pages/admin/GalleryManagementPage";
import { EditGalleryPage } from "./pages/admin/EditGalleryPage";
import { AdminImageGalleryPage } from "./pages/admin/AdminImageGalleryPage";
import { ImageSettingsPage } from "./pages/admin/ImageSettingsPage";
import { SiteSettingsPage } from "./pages/admin/SiteSettingsPage";
import { CategoryManagementPage } from "./pages/admin/CategoryManagementPage";
import { ContactSubmissionsPage } from "./pages/admin/ContactSubmissionsPage";
import { useFeaturedGalleries, usePublicGalleries } from "./hooks/useGalleries";
import { usePublicSiteSettings } from "./hooks/useSiteSettings";
import { PortfolioGrid } from "./components/portfolio/PortfolioGrid";
import { SEOHead } from "./components/seo/SEOHead";
import { ImagePreloader } from "./components/seo/ImagePreloader";
import { generateOrganizationStructuredData, getFullUrl } from "./utils/seo";
import { PageTracker } from "./hooks/usePageTracking";
import { useScrollTracking } from "./hooks/useScrollTracking";
import { useEngagementTime } from "./hooks/useEngagementTime";
import { useOutboundLinkTracking } from "./hooks/useOutboundLinkTracking";
import { SkipToContent } from "./components/common/SkipToContent";
import { useFocusManagement } from "./hooks/useFocusManagement";
import "./App.css";

function HomePage() {
  const navigate = useNavigate();
  const { data: galleries, isLoading, error } = useFeaturedGalleries();
  const { data: siteSettings, isLoading: settingsLoading } =
    usePublicSiteSettings();

  // Get hero image URL from settings
  const heroImageUrl = siteSettings?.settings?.hero_image_url;

  const homeDescription =
    "Professional photography portfolio by Mariah Blue. Specializing in authentic, unscripted moments captured with intention and care. Available for commissions and inquiries.";

  const structuredData = generateOrganizationStructuredData(
    getFullUrl("/"),
    heroImageUrl
  );

  // Debug: Log the hero image URL when it changes
  useEffect(() => {
    if (siteSettings?.settings) {
      console.log(
        "Hero image URL from settings:",
        siteSettings.settings.hero_image_url
      );
      console.log("All public settings:", siteSettings.settings);
    }
  }, [siteSettings]);

  const handleViewAll = () => {
    navigate("/galleries");
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        component="main"
        id="main-content"
        aria-busy="true"
        aria-live="polite"
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 8,
          minHeight: "100vh",
          alignItems: "center",
        }}
      >
        <CircularProgress aria-label="Loading galleries" />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="error">
          Failed to load galleries. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="main">
      <SEOHead
        title="Home"
        description={homeDescription}
        image={heroImageUrl}
        url={getFullUrl("/")}
        structuredData={structuredData}
      />
      {/* Preload critical hero image for better LCP */}
      {heroImageUrl && (
        <ImagePreloader imageUrl={heroImageUrl} critical={true} />
      )}
      <Hero imageUrl={settingsLoading ? undefined : heroImageUrl} />
      <Box
        component="section"
        id="featured-galleries"
        aria-label="Featured photography galleries"
      >
        <FeaturedGalleries
          galleries={galleries || []}
          onViewAll={handleViewAll}
        />
      </Box>
    </Box>
  );
}

function GalleriesPage() {
  const { data: galleries, isLoading, error } = usePublicGalleries();

  const galleriesDescription =
    "Browse all photography galleries by Mariah Blue. Explore collections of authentic, unscripted moments captured with intention and care.";

  const structuredData = [generateOrganizationStructuredData(getFullUrl("/"))];

  // Loading state
  if (isLoading) {
    return (
      <PageTransition>
        <Box
          component="main"
          id="main-content"
          aria-busy="true"
          aria-live="polite"
          sx={{
            minHeight: "100vh",
            pt: { xs: "80px", md: "80px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress aria-label="Loading galleries" />
        </Box>
      </PageTransition>
    );
  }

  // Error state
  if (error) {
    console.error("GalleriesPage error:", error);
    return (
      <PageTransition>
        <Container sx={{ mt: 14, mb: 8 }}>
          <Typography variant="h1" gutterBottom>
            Galleries
          </Typography>
          <Typography variant="body1" color="error">
            Failed to load galleries. Please try again later.
          </Typography>
          {process.env.NODE_ENV === "development" && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Error: {error instanceof Error ? error.message : String(error)}
            </Typography>
          )}
        </Container>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <SEOHead
        title="Galleries"
        description={galleriesDescription}
        url={getFullUrl("/galleries")}
        structuredData={structuredData}
      />
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          pt: 0,
          mt: 0,
        }}
      >
        <PortfolioGrid galleries={galleries || []} title="Galleries" />
      </Box>
    </PageTransition>
  );
}

function App() {
  const { isOpen, closeModal } = useLoginModal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useFocusManagement(); // Manage focus on route changes
  useScrollTracking(); // Track scroll depth
  useEngagementTime(); // Track time on page
  useOutboundLinkTracking(); // Track outbound link clicks

  const handleMenuOpen = () => setMobileMenuOpen(true);
  const handleMenuClose = () => setMobileMenuOpen(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <SkipToContent />
      <PageTracker />
      <ScrollToTop />
      <Header onMenuClick={handleMenuOpen} />
      <MobileMenu open={mobileMenuOpen} onClose={handleMenuClose} />

      <Box
        component="main"
        id="main-content"
        sx={{ flex: 1, paddingTop: "80px" }}
        tabIndex={-1}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/galleries" element={<GalleriesPage />} />
          <Route path="/gallery/:id" element={<GalleryDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/galleries"
            element={
              <ProtectedRoute>
                <GalleryManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/galleries/:id/edit"
            element={
              <ProtectedRoute>
                <EditGalleryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/images"
            element={
              <ProtectedRoute>
                <AdminImageGalleryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/image-settings"
            element={
              <ProtectedRoute>
                <ImageSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <SiteSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute>
                <CategoryManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contact-submissions"
            element={
              <ProtectedRoute>
                <ContactSubmissionsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>

      <Footer />
      <LoginModal open={isOpen} onClose={closeModal} />
    </Box>
  );
}

export default App;
